import { useEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";

/**
 * Mobile-only contextual floating action button.
 *
 * Renders ONLY on mobile (< md breakpoint) — desktop/tablet retain their
 * existing buttons. Accepts a small set of page-aware actions and triggers
 * the same dialogs the desktop buttons use (no duplicated workflows).
 *
 * Props
 *  - actions: [{ key, label, icon: LucideIcon, onClick, testid }]
 *  - testid (optional): root testid override (default 'mobile-fab')
 *
 * Behavior
 *  - Tap FAB → expand action list with subtle stagger animation
 *  - Tap action → invoke onClick + close
 *  - Tap outside / press Escape → close
 *  - Single action → tapping the FAB calls it directly (no menu)
 *  - Positioned to clear pagination + Emergent watermark
 */
export default function MobileFab({ actions = [], testid = "mobile-fab" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on Escape + tap-outside
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onClick);
    };
  }, [open]);

  if (!actions.length) return null;

  // Single-action shortcut: tap FAB triggers directly without expanding.
  const singleAction = actions.length === 1 ? actions[0] : null;

  const handleFabTap = () => {
    if (singleAction) {
      singleAction.onClick?.();
      return;
    }
    setOpen((v) => !v);
  };

  return (
    <>
      {/* Tap-outside backdrop — only when expanded, only on mobile */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] fade-up"
          style={{ animationDuration: "150ms" }}
          aria-hidden="true"
          onClick={() => setOpen(false)}
          data-testid={`${testid}-backdrop`}
        />
      )}

      <div
        ref={rootRef}
        className="md:hidden fixed right-4 z-50 flex flex-col items-end gap-2"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)" }}
        data-testid={testid}
      >
        {/* Expanded action list */}
        {open && !singleAction && (
          <ul
            className="flex flex-col items-end gap-2 mb-1"
            role="menu"
            aria-label="Quick actions"
          >
            {actions.map((a, i) => {
              const Icon = a.icon;
              return (
                <li
                  key={a.key}
                  className="fade-up"
                  style={{
                    animationDuration: "220ms",
                    animationDelay: `${i * 35}ms`,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      a.onClick?.();
                      setOpen(false);
                    }}
                    className="tap inline-flex items-center gap-2.5 pl-3.5 pr-4 h-11 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-[var(--text-primary)] shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] hover:border-[#E82127]/60 active:scale-[0.98] transition-all"
                    style={{ borderRadius: 999 }}
                    data-testid={a.testid || `${testid}-action-${a.key}`}
                    role="menuitem"
                  >
                    {Icon && (
                      <span
                        className="w-6 h-6 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-subtle)]"
                        style={{ borderRadius: 999 }}
                      >
                        <Icon className="w-3.5 h-3.5 text-[var(--text-primary)]" strokeWidth={1.75} />
                      </span>
                    )}
                    <span className="text-[13px] tracking-tight">{a.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* Main FAB */}
        <button
          type="button"
          onClick={handleFabTap}
          aria-expanded={open}
          aria-haspopup={singleAction ? "false" : "menu"}
          aria-label={
            singleAction ? singleAction.label : open ? "Close quick actions" : "Open quick actions"
          }
          className="tap relative w-14 h-14 flex items-center justify-center bg-[#E82127] text-white shadow-[0_10px_28px_-6px_rgba(232,33,39,0.55)] hover:bg-[#FF2A30] active:scale-95 transition-all"
          style={{
            borderRadius: 999,
            transitionProperty: "transform, background-color, box-shadow",
            transitionDuration: "220ms",
          }}
          data-testid={`${testid}-trigger`}
        >
          {open && !singleAction ? (
            <X className="w-6 h-6" strokeWidth={2.25} />
          ) : (
            <Plus className="w-6 h-6" strokeWidth={2.25} />
          )}
        </button>
      </div>
    </>
  );
}
